import React from "react";
import { useFormContext, Controller } from "react-hook-form";
import TextField from "@mui/material/TextField";
import "../../theme/variables.scss";

const textFieldStyles = {
  sx: {
    minWidth: "100px",
    "& .MuiFilledInput-root": {
      background: "white",
      "&:before": { display: "none" },
      "&:after": { display: "none" },
      borderRadius: "4px",
      outline: "1px solid var(--component-border)",
      fontFamily: "var(--auro-font-family-default)",
      fontSize: "16px",
      fontStyle: "normal",
      fontWeight: 450,
      lineHeight: "16px",
      height: "58px",
      cursor: "pointer",
      color: "var(--dark-blue-primary)",
      "&.Mui-error": {
        outline: "2px solid var(--color-error)"
      },
    },
    "& .MuiFilledInput-root:hover": {
      background: "white",
      outline: "2px solid var(--color-light-blue-primary)",
      color: "var(--dark-blue-primary)",
      "&.Mui-error": {
        outline: "2px solid var(--color-error) !important",
      },
    },
    "& .MuiFilledInput-root.Mui-focused": {
      background: "white",
      color: "var(--dark-blue-primary)",
      outline: "2px solid var(--color-light-blue-primary)",
      "&.Mui-error": {
        outline: "2px solid var(--color-error) !important",
      },
    },
    "& .MuiFilledInput-root input::placeholder": {
      color: "var(--text-placeholder-color)",
      fontSize: "16px",
    },
    "& .MuiInputLabel-root": {
      color: "var(--text-placeholder-color)",
      fontSize: "16px",
    },
    "& .MuiFormHelperText-root.Mui-error": {
      color: "var(--color-error)",
      fontSize: "var(--error-font-size)",
      fontWeight: 500,
      lineHeight: "1rem",
      margin: "var(--auro-size-50, 0.25rem) 0",
      fontFamily: "var(--auro-font-family-default)",
    },
  },
};


type MaterialUITextInputProps = {
  name?: string;
  label?: string;
  id?: string;
  value?: any;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  error?: boolean;
  helperText?: React.ReactNode;
  sx?: object;
  variant?: "filled" | "standard" | "outlined";
  classNameAdded?: string;
  required?: boolean | string;
  requiredMessage?: string;
  inputProps?: object;
  [key: string]: any;
};

const MaterialUITextInput = ({
  name,
  label,
  id = "custom-text-input",
  value,
  onChange,
  error,
  helperText,
  sx = {},
  variant = "filled",
  className,
  required,
  requiredMessage = "This field is required",
  inputProps = {},
  ...props
}: MaterialUITextInputProps) => {
  let formContext;
  try {
    formContext = useFormContext();
  } catch {
    formContext = undefined;
  }

  const mergedSx = { ...textFieldStyles.sx, ...sx };

  const defaultInputProps = {
    autoComplete: "off"
  };

  const mergedInputProps = { ...defaultInputProps, ...inputProps };

  if (formContext && formContext.control && name) {
    return (
      <Controller
        name={name}
        control={formContext.control}
        rules={{
          required: required ? requiredMessage : undefined
        }}
        render={({
          field: { onChange, onBlur, value, ref },
          fieldState: { error }
        }) => (
          <TextField
            className={className}
            id={id}
            label={label}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            inputRef={ref}
            required={!!required}
            error={!!error}
            helperText={error ? error.message : helperText}
            inputProps={mergedInputProps}
            InputLabelProps={{ required: false, ...props.InputLabelProps }}
            fullWidth
            variant={variant}
            sx={mergedSx}
            {...props}
          />
        )}
      />
    );
  }

  return (
    <TextField
      className={className}
      id={id}
      label={label}
      value={value}
      onChange={onChange}
      required={!!required}
      error={!!error}
      helperText={helperText}
      inputProps={mergedInputProps}
      fullWidth
      variant={variant}
      sx={mergedSx}
      {...props}
    />
  );
};

export default MaterialUITextInput;
